import { useEffect, useRef, useState } from "react";
import { Fingerprint, Mic, Camera, Radio, Sparkles, Download, Video, Copy, Loader2, ShieldCheck, ShieldAlert, ExternalLink, History, FileDown } from "lucide-react";
import axios, { API } from "@/lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { FaceCaptureModal } from "./FaceCaptureModal";

const FACE_ANGLES = [
  { key: "front", label: "Front" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
];

const VERDICT_COLOR = {
  MAISON_VERIFIED: "text-truth-green",
  SYNTHETIC_DETECTED: "text-sovereign",
  INCONCLUSIVE: "text-amber-400",
};

const getErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map((d) => d.msg || String(d)).join(", ");
  return fallback;
};

export const ExecutiveIdentityPanel = ({ auditId }) => {
  const [executive, setExecutive] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState("");
  const [legalPhone, setLegalPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [captureAngle, setCaptureAngle] = useState(null);
  const voiceInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const fetchExecutive = () => {
    axios
      .get(`${API}/audits/${auditId}/executive`)
      .then((res) => setExecutive(res.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  };

  useEffect(fetchExecutive, [auditId]);

  const fetchHistory = () => {
    axios
      .get(`${API}/audits/${auditId}/executive/verification-history`)
      .then((res) => setHistory(res.data))
      .catch(() => {});
  };

  useEffect(() => {
    if (executive) fetchHistory();
  }, [executive?.id]);

  const register = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/audits/${auditId}/executive`, { executive_name: name.trim(), legal_team_phone: legalPhone.trim() || null });
      setExecutive(data);
      toast.success("Executive Identity registered - now enroll a Master Voice and Face sample.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not register executive."));
    } finally {
      setBusy(false);
    }
  };

  const uploadVoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(`${API}/audits/${auditId}/executive/voice-sample`, form);
      toast.success("Master Voice Sample enrolled - Voice DNA registered.");
      fetchExecutive();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not process voice sample."));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const uploadFace = async (fileOrBlob, angle) => {
    if (!fileOrBlob) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", fileOrBlob, fileOrBlob.name || `${angle}-capture.jpg`);
    form.append("angle", angle);
    try {
      await axios.post(`${API}/audits/${auditId}/executive/face-sample`, form);
      toast.success(`Master Face Sample (${angle}) enrolled - raw image discarded, only geometry hash kept.`);
      fetchExecutive();
      setCaptureAngle(null);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not process face sample."));
    } finally {
      setBusy(false);
    }
  };

  const toggleProofOfLife = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/audits/${auditId}/executive/proof-of-life`);
      setExecutive((prev) => ({ ...prev, proof_of_life: data.proof_of_life }));
      toast.success(data.proof_of_life.active ? "Public Proof of Life issued." : "Proof of Life revoked.");
    } catch {
      toast.error("Could not update Proof of Life.");
    } finally {
      setBusy(false);
    }
  };

  const runProbes = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/audits/${auditId}/executive/reputation-probes`);
      setProbeResult(data.analysis);
      setExecutive((prev) => ({ ...prev, reputation_probes: { ...data, last_run_at: new Date().toISOString() } }));
      toast.success("Reputation Defense probes complete.");
    } catch {
      toast.error("Could not run reputation probes.");
    } finally {
      setBusy(false);
    }
  };

  const downloadBadge = () => {
    window.open(`${API}/audits/${auditId}/executive/watermark/badge`, "_blank");
  };

  const watermarkVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/audits/${auditId}/executive/watermark/video`, form, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "maison-sealed.mp4";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Maison Seal applied - watermarked video downloaded.");
    } catch {
      toast.error("Could not watermark video.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${executive.public_url}`);
      toast.success("Public verification link copied.");
    } catch {
      toast.error("Could not copy link.");
    }
  };

  const downloadHistoryPdf = () => {
    window.open(`${API}/audits/${auditId}/executive/verification-history/pdf`, "_blank");
  };

  if (!loaded) return null;

  return (
    <div className="rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6" data-testid="executive-identity-panel">
      <div className="mb-1 flex items-center gap-2">
        <Fingerprint className="h-4 w-4 text-[#D4AF37]" />
        <h2 className="font-heading text-xl font-bold text-white">Sovereign Identity Shield</h2>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Voice/Face DNA registry and deepfake verification for your named executive - a best-effort forensic signal, not a certified legal determination.
      </p>

      {!executive ? (
        <div className="flex flex-col gap-2 sm:flex-row" data-testid="register-executive-form">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Executive full name" data-testid="executive-name-input" />
          <Input value={legalPhone} onChange={(e) => setLegalPhone(e.target.value)} placeholder="Legal team phone (optional)" data-testid="executive-legal-phone-input" />
          <Button onClick={register} disabled={busy} className="gap-2 bg-[#D4AF37] text-obsidian hover:bg-[#D4AF37]/90" data-testid="register-executive-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Register Executive
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-surface-border bg-obsidian p-3">
            <div>
              <p className="text-sm font-semibold text-white" data-testid="executive-name-display">{executive.executive_name}</p>
              <a href={executive.public_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-openai-cyan hover:underline" data-testid="executive-public-link">
                {window.location.origin}{executive.public_url} <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <Button size="sm" variant="outline" onClick={copyPublicLink} className="gap-1 border-surface-border text-slate-200 hover:text-white" data-testid="copy-public-link-btn">
              <Copy className="h-3.5 w-3.5" /> Copy Link
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-surface-border bg-obsidian p-4" data-testid="voice-dna-card">
              <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Mic className="h-4 w-4" /> Voice DNA</p>
              {executive.has_voice_dna ? (
                <p className="text-xs text-truth-green" data-testid="voice-dna-status">Master Voice Sample registered</p>
              ) : (
                <p className="text-xs text-slate-500">Upload a 60-second clean speech sample.</p>
              )}
              {executive.voice_integrity_due && (
                <p className="mt-1.5 rounded border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-2 text-xs text-[#D4AF37]" data-testid="voice-integrity-nudge">
                  Integrity Check Required: Please refresh your Master Voice Sample to ensure S-DNA precision.
                </p>
              )}
              <input ref={voiceInputRef} type="file" accept="audio/*" onChange={uploadVoice} className="hidden" data-testid="voice-sample-file-input" />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => voiceInputRef.current?.click()} className="mt-2 gap-2 border-surface-border text-slate-200 hover:text-white" data-testid="upload-voice-sample-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />} {executive.has_voice_dna ? "Re-enroll" : "Upload Sample"}
              </Button>
            </div>
            <div className="rounded-md border border-surface-border bg-obsidian p-4" data-testid="face-dna-card">
              <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Camera className="h-4 w-4" /> Face DNA (3 Angles)</p>
              <p className="mb-2 text-xs text-slate-500">
                {executive.face_dna_complete
                  ? "All 3 angles registered - never stored raw."
                  : `${executive.face_angles_registered?.length || 0}/3 angles registered - never stored raw.`}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {FACE_ANGLES.map(({ key, label }) => {
                  const registered = executive.face_angles_registered?.includes(key);
                  return (
                    <div key={key} className="flex flex-col items-center gap-1">
                      <span className={`text-[10px] uppercase tracking-wide ${registered ? "text-truth-green" : "text-slate-500"}`}>{label}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => setCaptureAngle(key)}
                        className="w-full gap-1 border-surface-border px-2 text-slate-200 hover:text-white"
                        data-testid={`upload-face-sample-btn-${key}`}
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} {registered ? "Re-enroll" : "Capture"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="rounded-md border border-surface-border bg-obsidian p-4" data-testid="proof-of-life-card">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Radio className="h-4 w-4" /> Public Proof of Life</p>
            <p className="mb-2 text-xs text-slate-500">
              {executive.proof_of_life?.active
                ? `Active - issued ${new Date(executive.proof_of_life.issued_at).toLocaleString()}`
                : "Not issued - generate a time-stamped link to prove authenticity during a digital crisis."}
            </p>
            <Button size="sm" disabled={busy} onClick={toggleProofOfLife} className={executive.proof_of_life?.active ? "gap-2 border border-surface-border bg-transparent text-slate-200 hover:text-white" : "gap-2 bg-truth-green text-obsidian hover:bg-truth-green/90"} data-testid="toggle-proof-of-life-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} {executive.proof_of_life?.active ? "Revoke Proof of Life" : "Issue Proof of Life"}
            </Button>
          </div>

          <div className="rounded-md border border-surface-border bg-obsidian p-4" data-testid="reputation-defense-card">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><ShieldAlert className="h-4 w-4" /> Reputation Defense (Personal)</p>
            <Button size="sm" variant="outline" disabled={busy} onClick={runProbes} className="mb-2 gap-2 border-surface-border text-slate-200 hover:text-white" data-testid="run-reputation-probes-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Run AI Probes
            </Button>
            {(probeResult || executive.reputation_probes?.analysis) && (
              <div className="mt-2 space-y-1 text-xs text-slate-400" data-testid="reputation-probes-result">
                <p>Controversy: <span className={(probeResult || executive.reputation_probes.analysis).controversy_detected ? "text-sovereign" : "text-truth-green"}>{(probeResult || executive.reputation_probes.analysis).controversy_detected ? "Detected" : "None found"}</span> - {(probeResult || executive.reputation_probes.analysis).controversy_summary}</p>
                <p>Deepfakes: <span className={(probeResult || executive.reputation_probes.analysis).deepfake_mentions_detected ? "text-sovereign" : "text-truth-green"}>{(probeResult || executive.reputation_probes.analysis).deepfake_mentions_detected ? "Mentions found" : "None found"}</span> - {(probeResult || executive.reputation_probes.analysis).deepfake_summary}</p>
              </div>
            )}
          </div>

          <div className="rounded-md border border-[#D4AF37]/30 bg-obsidian p-4" data-testid="maison-seal-card">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Video className="h-4 w-4 text-[#D4AF37]" /> Maison Seal Watermark</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={downloadBadge} className="gap-2 border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid="download-seal-badge-btn">
                <Download className="h-3.5 w-3.5" /> Download Gold Seal (Print/Copy)
              </Button>
              <input ref={videoInputRef} type="file" accept="video/*" onChange={watermarkVideo} className="hidden" data-testid="watermark-video-file-input" />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => videoInputRef.current?.click()} className="gap-2 border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid="watermark-video-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Video className="h-3.5 w-3.5" />} Watermark a Video Clip
              </Button>
            </div>
          </div>

          <div className="rounded-md border border-surface-border bg-obsidian p-4" data-testid="verification-history-card">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-semibold text-white"><History className="h-4 w-4" /> Identity Audit Ledger &mdash; Verification History</p>
              <Button size="sm" variant="outline" onClick={downloadHistoryPdf} className="gap-1.5 border-surface-border text-slate-200 hover:text-white" data-testid="download-verification-history-pdf-btn">
                <FileDown className="h-3.5 w-3.5" /> Export PDF
              </Button>
            </div>
            {history.length === 0 ? (
              <p className="text-xs text-slate-500" data-testid="verification-history-empty">No verification attempts logged yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="verification-history-list">
                {history.map((h) => (
                  <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-surface-border/60 px-3 py-1.5 text-xs" data-testid={`verification-history-row-${h.id}`}>
                    <span className="text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</span>
                    <span className="uppercase text-slate-500">{h.source}</span>
                    <span className={VERDICT_COLOR[h.verdict] || "text-slate-300"}>{h.verdict}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {captureAngle && (
        <FaceCaptureModal
          angle={captureAngle}
          label={FACE_ANGLES.find((a) => a.key === captureAngle)?.label || captureAngle}
          busy={busy}
          onClose={() => setCaptureAngle(null)}
          onCapture={(blob) => uploadFace(blob, captureAngle)}
        />
      )}
    </div>
  );
};
